import { LocalStorageKeys } from "@/core/@types/types";
import { financasBaseUrl } from "../protocols";
import ToastificationNotificationException from "../exceptions/ToastificationNotificationException";

const redirectStorageKey = "f360.auth.redirects";
type SessionWindow = Window & { __f360LoginRedirecting?: boolean };
let tokenRequest: Promise<string> | undefined;

export class SessionRetryError extends Error {
  constructor() {
    super("Sua sessão foi atualizada. Tente realizar a operação novamente.");
    this.name = "SessionRetryError";
  }
}

export class SessionExpiredError extends Error {
  constructor() {
    super("Sessao expirada");
    this.name = "SessionExpiredError";
  }
}

export function normalizeSessionToken(value: unknown): string {
  if (typeof value !== "string") return "";
  try {
    const parsed = JSON.parse(value);

    return typeof parsed === "string" ? parsed.trim() : "";
  } catch {
    return value.trim();
  }
}

export function isSessionTokenValid(token: string): boolean {
  const parts = token.split(".");
  if (parts.length !== 3 || parts.some((part) => !/^[A-Za-z0-9_-]+$/.test(part))) return false;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));

    return typeof payload.exp === "number" && Number.isFinite(payload.exp) &&
      payload.exp > Math.floor(Date.now() / 1000);
  } catch {
    return false;
  }
}

export function sessionRequestUrl(url = "", baseURL = ""): string {
  const combined = baseURL && !/^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(url)
    ? baseURL.replace(/\/+$/, "") + "/" + url.replace(/^\/+/, "")
    : url;

  return new URL(combined, window.location.href).href;
}

export function isSessionLoginRedirecting(): boolean {
  return (window as SessionWindow).__f360LoginRedirecting === true;
}

function requestKey(method: string, url: string): string {
  const parsed = new URL(url, window.location.href);

  return method.toUpperCase() + " " + parsed.origin + parsed.pathname.replace(/\/+$/, "");
}

export function isAuthenticationRequest(url: string): boolean {
  return /^\/(?:api\/)?(?:Home(?:\/|$)|v2\/auth\/?$|refreshCookie\/?$|health(?:check)?\/?$)/i
    .test(new URL(url, window.location.href).pathname);
}

export function redirectToSessionLogin(method: string, url: string): void {
  const state = window as SessionWindow;
  if (state.__f360LoginRedirecting || /^\/Home(?:\/|$)/i.test(window.location.pathname)) return;
  state.__f360LoginRedirecting = true;
  let repeated = false;
  try {
    const marks: Record<string, boolean> = JSON.parse(sessionStorage.getItem(redirectStorageKey) || "{}");
    const key = requestKey(method, url);
    repeated = marks[key] === true;
    marks[key] = true;
    sessionStorage.setItem(redirectStorageKey, JSON.stringify(marks));
  } catch {
    // Sem storage nao e possivel reconhecer um retorno automatico anterior.
    repeated = true;
  }
  for (const key of [LocalStorageKeys.Token, LocalStorageKeys.TokenNewHorizons, LocalStorageKeys.UserProfile, LocalStorageKeys.userRules]) {
    try { localStorage.removeItem(key); } catch { /* Login continua sem storage. */ }
  }
  const login = new URL("Home/Index", sessionRequestUrl("", financasBaseUrl || window.location.origin));
  login.searchParams.set("redirect", window.location.pathname + window.location.search + window.location.hash);
  if (repeated) login.searchParams.set("reauth", "1");
  window.location.assign(login.origin === window.location.origin ? login.pathname + login.search : login.href);
}

export function requireSessionToken(value: unknown, _method: string, _url: string): string {
  const token = normalizeSessionToken(value);
  if (isSessionLoginRedirecting()) throw new SessionExpiredError();
  if (isSessionTokenValid(token)) return token;
  throw new Error("O servidor não retornou um token de autenticação válido.");
}

export function refreshSessionToken(): Promise<string> {
  if (isSessionLoginRedirecting()) return Promise.reject(new SessionExpiredError());
  if (!tokenRequest) {
    const url = import.meta.env.DEV ? "/api/v2/auth" : sessionRequestUrl("/v2/auth", financasBaseUrl || window.location.origin);
    tokenRequest = (async () => {
      const response = await fetch(url, { credentials: "include", cache: "no-store", headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" } });
      if (response.redirected && (() => {
        const target = new URL(response.url);

        return target.origin === new URL(url, window.location.href).origin &&
          (target.pathname === "/" || /^\/Home(?:\/(?:Index|Login))?\/?$/i.test(target.pathname));
      })()) {
        redirectToSessionLogin("GET", url);
        throw new SessionExpiredError();
      }
      await handleSessionResponse(response.status, "GET", url, response.headers.get("X-Location"));
      if (!response.ok) throw new Error("Não foi possível obter a sessão (HTTP " + response.status + ").");
      const data = await response.json();
      const token = requireSessionToken(data?.Token, "GET", url);
      localStorage.setItem(LocalStorageKeys.TokenNewHorizons, JSON.stringify(token));
      localStorage.setItem(LocalStorageKeys.Token, JSON.stringify(token));
      clearSessionOperationFailure("GET", url);

      return token;
    })().finally(() => { tokenRequest = undefined; });
  }

  return tokenRequest;
}

export async function ensureSessionToken(value: unknown, _method: string, _url: string): Promise<string> {
  if (isSessionLoginRedirecting()) throw new SessionExpiredError();
  const token = normalizeSessionToken(value);

  return isSessionTokenValid(token) ? token : refreshSessionToken();
}

export function clearSessionOperationFailure(method: string, url: string): void {
  try {
    const marks: Record<string, boolean> = JSON.parse(sessionStorage.getItem(redirectStorageKey) || "{}");
    delete marks[requestKey(method, url)];
    sessionStorage.setItem(redirectStorageKey, JSON.stringify(marks));
  } catch { /* A indisponibilidade do storage nao altera a resposta. */ }
}

export function handleSessionResponse(status: number, method: string, url: string, location?: string | null): void | Promise<never> {
  if (/^\/(?:api\/)?Home(?:\/|$)/i.test(new URL(url, window.location.href).pathname)) return;
  const loginRedirect = status === 302 && location && (() => {
    const target = new URL(location, window.location.href);

    return target.origin === window.location.origin && (target.pathname === "/" || /^\/Home\/(?:Index|Login)\/?$/i.test(target.pathname));
  })();
  if (status === 401 || loginRedirect) {
    if (/^\/(?:api\/)?v2\/auth\/?$/i.test(new URL(url, window.location.href).pathname)) {
      redirectToSessionLogin(method, url);
      throw new SessionExpiredError();
    }

    return refreshSessionToken().then(() => {
      const error = new SessionRetryError();
      new ToastificationNotificationException().handle(error.message);
      throw error;
    });
  }
  // O bootstrap so funciona quando o corpo contem um Bearer utilizavel.
  const bootstrap = /^\/(?:api\/)?v2\/auth\/?$/i.test(new URL(url, window.location.href).pathname);
  if (status >= 200 && status < 300 && !bootstrap) clearSessionOperationFailure(method, url);
}
